import { isNotDefined } from '@sgme/fp';
import { HighchartsReact } from 'highcharts-react-official';
import type { Options, PointOptionsObject } from 'highcharts/highstock';
import Highcharts from 'highcharts/highstock';
import 'highcharts/modules/annotations';
import { useSgBsTheme } from 'hooks/useSgBsTheme';
import { sanitizeLocaleCode } from 'locales/utils';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { toHighchartsTheme } from 'utils/highcharts';
import { capitalizeFirstletter } from 'utils/string';

import './style.scss';

interface IPoint {
  x: number;
  y: number;
  id?: PointType;
}
interface PointWithType extends PointOptionsObject {
  type?: PointType;
}

export interface IAxisNames {
  x: string;
  y: string;
}

export interface PayoffGraphProps {
  axisNames?: IAxisNames;
  points: IPoint[];
}

export type PointType =
  | 'Ki'
  | 'Bonus'
  | 'Cap'
  | 'Strike'
  | 'Zc'
  | 'StrikeLongCall'
  | 'StrikeShortCall'
  | 'StrikeLongPut'
  | 'StrikeShortPut'
  | 'Airbag';

export function PayoffGraph(props: PayoffGraphProps) {
  const { formatMessage, locale } = useIntl();
  const theme = useSgBsTheme();
  const xAxisName = capitalizeFirstletter(props.axisNames?.x ?? '');
  const yAxisName = capitalizeFirstletter(props.axisNames?.y ?? '');

  const points = useMemo(
    () =>
      props.points.map(({ id, ...point }): PointWithType => ({
        ...point,
        type: id,
      })),
    [props.points],
  );

  const options: Options = {
    chart: { styledMode: true },
    title: { text: undefined },
    series: [
      {
        type: 'line',
        data: points,
      },
    ],
    credits: {
      enabled: false,
    },
    lang: { locale: sanitizeLocaleCode(locale) },
    plotOptions: { line: { marker: { enabled: true } } },
    accessibility: { enabled: false },
    exporting: {
      enabled: true,
      accessibility: {
        enabled: true,
      },
      allowHTML: false,
      allowTableSorting: true,
      buttons: {
        contextButton: {
          enabled: true,
          menuItems: ['viewFullscreen', 'printChart', 'separator', 'downloadPNG', 'downloadJPEG', 'downloadSVG'],
        },
      },
    },
    xAxis: {
      labels: {
        format: '{value} %',
      },
      title: {
        text: xAxisName,
      },
    },
    yAxis: {
      title: {
        text: yAxisName,
      },
      labels: {
        format: '{value} %',
      },
    },
    tooltip: {
      formatter() {
        // Round to 4 decimals then remove trailing zeros
        const x = +Number(this.x).toFixed(4);
        const y = +Number(this.y).toFixed(4);

        const { type } = this.options as PointWithType;
        const label = isNotDefined(type) ? undefined : formatMessage({ id: `graph.annotations.${type}` });

        return `${label ? `<b>${label}</b><br><br>` : ''}${xAxisName}: ${x} %<br>${yAxisName}: ${y} %`;
      },
      shared: true,
    },
    legend: {
      enabled: false,
    },
  };

  return (
    <div className="payoff-graph-container">
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        containerProps={{ className: toHighchartsTheme(theme) }}
      />
    </div>
  );
}
